/**
 * Hook to automatically track outbound link clicks on the page
 * Attaches click listeners to all external links
 */

import { useEffect } from "react";
import { trackOutboundLink, trackSocialClick } from "../utils/analytics";

/**
 * Hook that automatically tracks all outbound link clicks on the page
 * 
 * Usage: Add useOutboundLinkTracking() to your App component
 */
export function useOutboundLinkTracking(): void {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      
      if (!link || !link.href) return;

      const url = new URL(link.href, window.location.href);
      const isExternal = url.hostname !== window.location.hostname;
      const linkText = link.textContent?.trim() || "";
      const href = link.href;

      // Track external links
      if (isExternal) {
        // Check if it's a social media link
        const hostname = url.hostname.toLowerCase();
        if (hostname.includes("instagram.com")) {
          trackSocialClick("Instagram", href);
        } else if (hostname.includes("facebook.com")) {
          trackSocialClick("Facebook", href);
        } else if (hostname.includes("twitter.com") || hostname.includes("x.com")) {
          trackSocialClick("Twitter", href);
        } else if (hostname.includes("pinterest.com")) {
          trackSocialClick("Pinterest", href);
        } else {
          trackOutboundLink(href, linkText);
        }
      }
    };

    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);
}
